import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import authenticate from '../../utils/Authenticate';
import displayError from '../../utils/ErrorHandler';

// SuperAdmin-only screen to create a "Silat Viewer" user — read-only access to
// the Silat / Silat Commitments dashboards (view + export only, no edit/approve).
// Modeled directly on AddLogisticsUser.jsx.
const AddSilatViewerUser = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', its: '', username: '', password: '' });
  const [notification, setNotification] = useState({ message: '', type: '' });

  // Jamiat access restriction: list of broad Jamiat group names (jamiat_name) to
  // choose from, and the subset selected for this user. Empty selection = no
  // restriction (unrestricted access to all jamiats' commitments/data).
  const [allJamiats, setAllJamiats] = useState([]);
  const [selectedJamiats, setSelectedJamiats] = useState([]);
  const [jamiatsLoading, setJamiatsLoading] = useState(true);

  useEffect(() => {
    async function startup() {
      if (!user) {
        const u = await authenticate(setUser, navigate);
        if (!u) return;
        if (u.role !== 'SuperAdmin') { navigate('/'); return; }
      } else if (user.role !== 'SuperAdmin') {
        navigate('/');
        return;
      }
      fetchJamiats();
    }
    startup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchJamiats = async () => {
    setJamiatsLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/jamiat/get`, { withCredentials: true });
      const names = res?.data?.data?.jamiat_name || [];
      setAllJamiats([...names].sort((a, b) => a.localeCompare(b)));
    } catch (err) {
      const errorMessage = displayError(err, navigate, setUser);
      if (errorMessage) setNotification({ message: errorMessage, type: 'error' });
    } finally {
      setJamiatsLoading(false);
    }
  };

  const toggleJamiat = (jamiatName) => {
    setSelectedJamiats((prev) =>
      prev.includes(jamiatName) ? prev.filter((j) => j !== jamiatName) : [...prev, jamiatName]
    );
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const submitData = (e) => {
    e.preventDefault();
    const payload = {
      username: formData.username,
      password: formData.password,
      name: formData.name,
      role: 'Silat Viewer',
      its: formData.its,
      zone: {},
      allowedJamiats: selectedJamiats
    };

    axios
      .post(`${process.env.REACT_APP_API_URI}/api/users/register`, payload, { withCredentials: true })
      .then(() => {
        setNotification({ message: 'Silat Viewer user created successfully!', type: 'success' });
        setFormData({ name: '', its: '', username: '', password: '' });
        setSelectedJamiats([]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((err) => {
        const errorMessage = displayError(err, navigate, setUser);
        if (errorMessage) {
          setNotification({ message: errorMessage, type: 'error' });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-4 lg:px-8">
      <h2 className="text-xl font-semibold text-fmb mb-1">Add Silat Viewer User</h2>
      <p className="text-sm text-gray-600 mb-4">
        Read-only access to Silat Commitments &amp; Silat Dashboard (view and export reports; no edit/approve).
      </p>

      {notification.message && (
        <div
          className={`m-2 mb-4 p-4 rounded ${
            notification.type === 'success'
              ? 'bg-green-200 border-green-600 text-green-600'
              : 'bg-red-200 border-red-600 text-red-600'
          }`}
        >
          {notification.message}
        </div>
      )}

      <form className="space-y-6" onSubmit={submitData}>
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">Full Name</label>
          <div className="mt-2">
            <input
              name="name"
              type="text"
              placeholder="Full name"
              value={formData.name}
              onChange={handleChange}
              required
              className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">ITS</label>
          <div className="mt-2">
            <input
              name="its"
              type="text"
              placeholder="ITS number"
              value={formData.its}
              onChange={handleChange}
              className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">Username</label>
          <div className="mt-2">
            <input
              name="username"
              type="text"
              placeholder="Username"
              value={formData.username}
              onChange={handleChange}
              required
              className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">Password</label>
          <div className="mt-2">
            <input
              name="password"
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              required
              className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium leading-6 text-gray-900">Jamiat Access</label>
            {allJamiats.length > 0 && (
              <div className="flex gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => setSelectedJamiats(allJamiats)}
                  className="text-fmb hover:underline"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedJamiats([])}
                  className="text-gray-500 hover:underline"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 mb-2">
            Select which jamiats' Silat &amp; Zabihat commitments this user can view. Leave everything
            unchecked to give unrestricted access to all jamiats.
          </p>
          <div className="mt-1 max-h-48 overflow-y-auto rounded-2xl border border-gray-300 p-3">
            {jamiatsLoading ? (
              <p className="text-sm text-gray-500">Loading jamiats...</p>
            ) : allJamiats.length === 0 ? (
              <p className="text-sm text-gray-500">No jamiats found.</p>
            ) : (
              allJamiats.map((jamiatName) => (
                <label key={jamiatName} className="flex items-center gap-2 py-1 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={selectedJamiats.includes(jamiatName)}
                    onChange={() => toggleJamiat(jamiatName)}
                    className="rounded border-gray-300 text-fmb focus:ring-fmb"
                  />
                  {jamiatName}
                </label>
              ))
            )}
          </div>
          {selectedJamiats.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {selectedJamiats.length} jamiat{selectedJamiats.length > 1 ? 's' : ''} selected — access will be
              restricted to only these.
            </p>
          )}
        </div>

        <button
          type="submit"
          className="flex w-full justify-center rounded-full text-white bg-fmb px-3 py-3 text-sm font-semibold leading-6 shadow-sm hover:bg-indigo-500"
        >
          CREATE SILAT VIEWER USER
        </button>
      </form>
    </div>
  );
};

export default AddSilatViewerUser;
